<script setup lang="ts">
  import { ref } from 'vue'
  import { Button } from '@/components/ui/button'
  import { Calendar, X } from 'lucide-vue-next'
  import { useDateRangeStore } from '@/store/dateRangeStore'
  import dayjs from 'dayjs'

  const props = defineProps<{ iconOnly?: boolean }>()

  // Define emits
  const emit = defineEmits(['dateRangeChanged'])

  // Track selected range for highlighting
  const selectedRange = ref<string | null>('last60days')

  const dateRangeStore = useDateRangeStore()
  const isPopoverOpen = ref(false)

  // Local state for the date pickers
  const localStartDate = ref(dayjs(dateRangeStore.startDate))
  const localEndDate = ref(dayjs(dateRangeStore.endDate))

  // Date validation functions
  const disabledStartDate = (current: dayjs.Dayjs) => {
    // Can't select dates after the end date (compare at day granularity)
    return current && current.startOf('day') > dayjs(localEndDate.value).startOf('day')
  }

  const disabledStartTime = (current: dayjs.Dayjs) => {
    const end = dayjs(localEndDate.value)
    if (!current || !current.isSame(end, 'day')) return {}
    const endHour = end.hour()
    const endMinute = end.minute()
    const endSecond = end.second()
    return {
      disabledHours: () => Array.from({ length: 24 - endHour - 1 }, (_, i) => endHour + 1 + i),
      disabledMinutes: (hour: number) => {
        if (hour < endHour) return []
        return Array.from({ length: 60 - endMinute - 1 }, (_, i) => endMinute + 1 + i)
      },
      disabledSeconds: (hour: number, minute: number) => {
        if (hour < endHour || (hour === endHour && minute < endMinute)) return []
        return Array.from({ length: 60 - endSecond }, (_, i) => endSecond + i)
      }
    }
  }

  const disabledEndDate = (current: dayjs.Dayjs) => {
    // Can't select dates before the start date (compare at day granularity)
    return current && current.startOf('day') < dayjs(localStartDate.value).startOf('day')
  }

  const disabledEndTime = (current: dayjs.Dayjs) => {
    const start = dayjs(localStartDate.value)
    if (!current || !current.isSame(start, 'day')) return {}
    const startHour = start.hour()
    const startMinute = start.minute()
    const startSecond = start.second()
    return {
      disabledHours: () => Array.from({ length: startHour }, (_, i) => i),
      disabledMinutes: (hour: number) => {
        if (hour > startHour) return []
        return Array.from({ length: startMinute }, (_, i) => i)
      },
      disabledSeconds: (hour: number, minute: number) => {
        if (hour > startHour || (hour === startHour && minute > startMinute)) return []
        return Array.from({ length: startSecond + 1 }, (_, i) => i)
      }
    }
  }

  // Format date for display
  function formatDateDisplay(dateString: string) {
    return dayjs(dateString).format('MMM D, YYYY')
  }

  // Apply the selected date range
  function applyDateRange() {
    const startDate = dayjs(localStartDate.value)
    const endDate = dayjs(localEndDate.value)

    dateRangeStore.setDateRange(startDate.format('YYYY-MM-DDTHH:mm:ssZ'), endDate.format('YYYY-MM-DDTHH:mm:ssZ'))
    isPopoverOpen.value = false
    // Emit event to notify parent component to refresh data
    emit('dateRangeChanged')

    // Clear selected range if it's a custom selection
    if (!selectedRange.value) {
      // This is a manual date selection, not a quick range
      selectedRange.value = null
    }
  }

  // Reset to default date range (60 days)
  function resetDateRange() {
    dateRangeStore.resetDateRange()
    localStartDate.value = dayjs(dateRangeStore.startDate)
    localEndDate.value = dayjs(dateRangeStore.endDate)
    selectedRange.value = 'last60days' // Set default selection to 60 days
  }

  // Clear the date range filter
  function clearDateRange() {
    dateRangeStore.toggleActive(false)
    // Reset the selected range when clearing the filter
    selectedRange.value = null
    // Emit event to notify parent component to refresh data
    emit('dateRangeChanged')
  }

  // Set quick date ranges
  function setQuickRange(range: string) {
    const now = dayjs()
    let start: dayjs.Dayjs
    let end: dayjs.Dayjs = now

    switch (range) {
      case 'today':
        start = now.startOf('day')
        end = now.endOf('day')
        break
      case 'yesterday':
        start = now.subtract(1, 'day').startOf('day')
        end = now.subtract(1, 'day').endOf('day')
        break
      case 'last7days':
        start = now.subtract(7, 'days').startOf('day')
        end = now.endOf('day')
        break
      case 'last30days':
        start = now.subtract(30, 'days').startOf('day')
        end = now.endOf('day')
        break
      case 'last60days':
        start = now.subtract(60, 'days').startOf('day')
        end = now.endOf('day')
        break
      case 'last90days':
        start = now.subtract(90, 'days').startOf('day')
        end = now.endOf('day')
        break
      case 'lastMonth':
        start = now.subtract(1, 'month').startOf('month')
        end = now.subtract(1, 'month').endOf('month')
        break
      case 'last365days':
        start = now.subtract(365, 'days').startOf('day')
        end = now.endOf('day')
        break
      case 'thisMonth':
        start = now.startOf('month')
        end = now.endOf('month')
        break
      default:
        start = now.subtract(60, 'days').startOf('day')
        end = now.endOf('day')
    }

    // Update local dates
    localStartDate.value = start
    localEndDate.value = end

    // Store the selected range for highlighting
    selectedRange.value = range

    // Automatically apply the date range with local time and UTC offset
    dateRangeStore.setDateRange(start.format('YYYY-MM-DDTHH:mm:ssZ'), end.format('YYYY-MM-DDTHH:mm:ssZ'))

    // Close the popover
    isPopoverOpen.value = false

    // Emit event to notify parent component to refresh data
    emit('dateRangeChanged')
  }
</script>

<template>
  <div class="date-range-selector flex items-center gap-2 shadow">
    <a-popover
      v-model:open="isPopoverOpen"
      trigger="click"
      placement="bottomRight"
      :overlayClassName="'date-range-popover'"
    >
      <template #content>
        <div class="w-[550px] p-4">
          <div class="mb-4 flex items-center justify-between">
            <h3 class="text-lg font-medium">Select Date Range</h3>
            <div class="flex gap-2">
              <Button size="sm" variant="outline" @click="resetDateRange">Reset</Button>
              <Button
                size="sm"
                variant="outline"
                :disabled="!localStartDate || !localEndDate"
                @click="applyDateRange"
              >
                Apply
              </Button>
            </div>
          </div>

          <div class="flex gap-4">
            <div class="flex-1">
              <label class="mb-1 block text-sm font-medium">Start Date</label>
              <a-date-picker
                v-model:value="localStartDate"
                class="w-full"
                :disabledDate="disabledStartDate"
                :disabledTime="disabledStartTime"
                showTime
              />
            </div>
            <div class="flex-1">
              <label class="mb-1 block text-sm font-medium">End Date</label>
              <a-date-picker
                v-model:value="localEndDate"
                class="w-full"
                :disabledDate="disabledEndDate"
                :disabledTime="disabledEndTime"
                showTime
              />
            </div>
          </div>

          <div class="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              :variant="selectedRange === 'today' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'today' }"
              @click="setQuickRange('today')"
            >
              Today
            </Button>
            <Button
              size="sm"
              :variant="selectedRange === 'yesterday' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'yesterday' }"
              @click="setQuickRange('yesterday')"
            >
              Yesterday
            </Button>
            <Button
              size="sm"
              :variant="selectedRange === 'thisMonth' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'thisMonth' }"
              @click="setQuickRange('thisMonth')"
            >
              This Month
            </Button>
            <Button
              size="sm"
              :variant="selectedRange === 'lastMonth' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'lastMonth' }"
              @click="setQuickRange('lastMonth')"
            >
              Last Month
            </Button>

            <Button
              size="sm"
              :variant="selectedRange === 'last7days' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'last7days' }"
              @click="setQuickRange('last7days')"
            >
              Last 7 Days
            </Button>
            <Button
              size="sm"
              :variant="selectedRange === 'last30days' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'last30days' }"
              @click="setQuickRange('last30days')"
            >
              Last 30 Days
            </Button>

            <Button
              size="sm"
              :variant="selectedRange === 'last60days' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'last60days' }"
              @click="setQuickRange('last60days')"
            >
              Last 60 Days
            </Button>
            <Button
              size="sm"
              :variant="selectedRange === 'last90days' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'last90days' }"
              @click="setQuickRange('last90days')"
            >
              Last 90 Days
            </Button>

            <Button
              size="sm"
              :variant="selectedRange === 'last365days' ? 'default' : 'outline'"
              :class="{ 'bg-primary text-white': selectedRange === 'last365days' }"
              @click="setQuickRange('last365days')"
            >
              Last 365 Days
            </Button>
          </div>
        </div>
      </template>

      <a-tooltip title="Filter Dates" placement="bottom">
        <button
          class="toggle-button small-button hover:bg-gray-10 flex items-center rounded border px-4 py-2"
          :class="{ 'bg-primary text-white': dateRangeStore.isActive }"
        >
          <Calendar class="h-6 w-6 select-none focus:outline-none" />
          <span v-if="!props.iconOnly && !dateRangeStore.isActive" class="ml-2">Date Range</span>
          <span v-if="!props.iconOnly && dateRangeStore.isActive" class="ml-2">
            {{ formatDateDisplay(dateRangeStore.startDate) }} -
            {{ formatDateDisplay(dateRangeStore.endDate) }}
          </span>
        </button>
      </a-tooltip>
    </a-popover>

    <a-tooltip v-if="dateRangeStore.isActive" title="Clear Date Filter" placement="bottom">
      <button
        class="clear-button small-button ml-[-10px] flex h-10 w-10 items-center justify-center rounded border"
        @click="clearDateRange"
      >
        <X class="h-6 w-6 select-none focus:outline-none" />
      </button>
    </a-tooltip>
  </div>
</template>

<style scoped>
  .date-range-button.active {
    background-color: var(--primary-color, #1890ff);
    color: white;
  }
  .toggle-button {
    border-right: none;
    border-top-left-radius:  0.25rem;
    border-bottom-left-radius:  0.25rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .clear-button {
    border-left: none;
    height: 42px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    margin-left: -10px;
  }
</style>
